import { t, tn, type Language } from "../lib/i18n";

interface ReasoningTraceProps {
  /** The parsed F17a chain-of-thought steps. Empty when none was
   * recorded (deterministic classification) OR when withheld by the
   * tenant policy — `visible` disambiguates the two. */
  trace: string[];
  /** Mirrors the server's `reasoning_trace_visible` field
   * (`dashboard.show_reasoning_trace`). When false the steps were
   * withheld server-side; we render the policy note, never the rows. */
  visible: boolean;
  language: Language;
}

/** Render the agent's reasoning trace as one row per step (mirrors the
 * operator workbook Pane 10 `mv-expand` shape). Three states:
 *
 * * `visible=false` → "hidden by policy" note.
 * * `visible=true` + empty → "no reasoning recorded" note.
 * * `visible=true` + steps → numbered list. */
export function ReasoningTrace({
  trace,
  visible,
  language,
}: ReasoningTraceProps): JSX.Element {
  return (
    <section
      aria-label={t("reasoning.heading", language)}
      className="mt-4 rounded-[2px] border border-kwanza-divider bg-kwanza-surface p-4"
    >
      <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-kwanza-inkSoft">
        {t("reasoning.heading", language)}
      </h3>
      {!visible ? (
        <p className="text-sm text-kwanza-inkSoft" data-testid="reasoning-hidden">
          {t("reasoning.hidden", language)}
        </p>
      ) : trace.length === 0 ? (
        <p className="text-sm text-kwanza-inkSoft" data-testid="reasoning-empty">
          {t("reasoning.empty", language)}
        </p>
      ) : (
        // Collapsed by default (F37 §12.3) — the trace is AI-speak detail the
        // accountant persona rarely needs. Native <details> keeps the steps
        // in the DOM (audit/e2e can still count them) while hiding them
        // visually until the user opts in; <summary> is keyboard-accessible
        // for free.
        <details data-testid="reasoning-details">
          <summary className="cursor-pointer text-sm font-bold text-kwanza-accentText">
            {tn(
              trace.length === 1
                ? "reasoning.toggle.one"
                : "reasoning.toggle.other",
              { n: trace.length },
              language,
            )}
          </summary>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            {trace.map((step, i) => (
              <li
                key={i}
                className="text-sm text-kwanza-ink"
                data-testid="reasoning-step"
              >
                {step}
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}
